import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const LAST_UPDATED  = 'May 21, 2026';
const APP_NAME      = 'Solvi';
const CONTACT_EMAIL = 'support@solvi.chat';

export default function Terms() {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-inner container">
          <Link to="/" className="legal-logo">{APP_NAME}</Link>
          <nav className="legal-nav">
            <Link to="/terms" className="active">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </nav>
        </div>
      </header>

      <main className="legal-main container">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

        <section>
          <p>
            Please read these Terms of Service ("Terms") carefully before using Solvi.
            By accessing or using the platform you agree to be bound by these Terms.
            If you do not agree, do not use the service.
          </p>
        </section>

        <section>
          <h2>1. Description of Service</h2>
          <p>
            Solvi provides a SaaS platform that lets businesses build AI-powered chatbots
            and connect them to messaging channels including Facebook Messenger, Instagram
            Direct, and WhatsApp Business. The service includes a web dashboard, a public
            chat widget, a knowledge base, and social channel integrations via Meta's Graph API.
          </p>
        </section>

        <section>
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old and have the legal authority to enter into these
            Terms on behalf of yourself or your organisation. By using Solvi you represent that
            you meet these requirements.
          </p>
        </section>

        <section>
          <h2>3. Account Responsibilities</h2>
          <ul>
            <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
            <li>You are responsible for all activity that occurs under your account.</li>
            <li>You must notify us immediately of any unauthorised access at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</li>
            <li>You must provide accurate information when registering.</li>
          </ul>
        </section>

        <section>
          <h2>4. Acceptable Use</h2>
          <p>You agree <strong>not</strong> to use Solvi to:</p>
          <ul>
            <li>Violate any applicable law or regulation</li>
            <li>Send spam, unsolicited messages, or bulk promotional content</li>
            <li>Impersonate any person or entity</li>
            <li>Transmit malware, viruses, or harmful code</li>
            <li>Scrape, crawl, or harvest data from the platform without permission</li>
            <li>Circumvent usage limits or access controls</li>
            <li>Violate Meta's Platform Terms or Developer Policies</li>
            <li>Deploy chatbots that engage in harassment, hate speech, or deception</li>
          </ul>
        </section>

        <section>
          <h2>5. Meta Platform Integrations</h2>
          <p>
            Connecting your Facebook Pages, Instagram accounts, or WhatsApp Business numbers
            is subject to Meta's Platform Terms of Service and Developer Policies. You are
            solely responsible for ensuring your use of the Meta integrations complies with
            Meta's policies. Solvi is not affiliated with Meta Platforms, Inc.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            You retain ownership of content you upload (knowledge base documents, chatbot
            configurations, system prompts). You grant Solvi a limited licence to process
            and store that content solely to provide the service. Solvi retains all rights
            to the platform, software, and interface.
          </p>
        </section>

        <section>
          <h2>7. AI-Generated Content</h2>
          <p>
            Responses generated by the AI are produced by third-party language models (Groq / Llama).
            Solvi does not guarantee the accuracy, completeness, or appropriateness of AI responses.
            You are responsible for reviewing and supervising your chatbot's behaviour before
            deploying it to end-users.
          </p>
        </section>

        <section>
          <h2>8. Service Availability</h2>
          <p>
            We aim for high availability but do not guarantee uninterrupted service. We may
            perform maintenance, upgrades, or emergency fixes that temporarily affect availability.
            We are not liable for losses resulting from downtime.
          </p>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Solvi and its affiliates will not be liable
            for any indirect, incidental, special, consequential, or punitive damages arising from
            your use of the service, even if advised of the possibility of such damages. Our total
            liability for any claim is limited to the amount you paid us in the 12 months preceding
            the claim.
          </p>
        </section>

        <section>
          <h2>10. Disclaimer of Warranties</h2>
          <p>
            The service is provided "as is" and "as available" without warranties of any kind,
            express or implied, including but not limited to merchantability, fitness for a
            particular purpose, and non-infringement.
          </p>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>
            You may delete your account at any time from the dashboard settings. We may suspend
            or terminate your account if you violate these Terms, with or without notice. Upon
            termination, your data will be deleted in accordance with our Privacy Policy.
          </p>
        </section>

        <section>
          <h2>12. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of material changes
            by email or in-app notice. Continued use of the service after the effective date
            constitutes acceptance.
          </p>
        </section>

        <section>
          <h2>13. Governing Law</h2>
          <p>
            These Terms are governed by the laws of Bangladesh. Disputes will be resolved in
            the courts of Dhaka, Bangladesh.
          </p>
        </section>

        <section>
          <h2>14. Contact</h2>
          <p>
            Questions about these Terms? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <p>© {new Date().getFullYear()} Solvi. <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link></p>
        </div>
      </footer>
    </div>
  );
}
